import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Button, OffcanvasProps } from 'react-bootstrap';
import ClientOnly from 'ClientOnly';
import { LoadingArea } from 'ui/Loading';
import ResponsiveImage from 'ui/Image';
import ImageSlider from '@components/ImageSlider';
import SupplierPackages from './SupplierPackages';
import SharePackageButton from './SharePackageButton';
import SupplierPackageSummary from './SupplierPackageSummary';
import { NotificationContext } from '@components/Notification/Notification';
import { Notification } from './SupplierPackageExtras';
import { getAsset } from '@helper/getConfigValues'

import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/free-solid-svg-icons';

import { useVwp } from '../VwpContext';
import { getVwpSupplier } from '@api/getVwpSupplier';

import { VwpOrderCategory, VwpSupplier } from '../types';
import { SupplierDetailsProps, SupplierVideoModalProps } from './types';

import { setSessionCookie } from '@helper/cookies';
import { VWP_COOKIE_REGION } from '@helper/constants';
import { removeQueryParam } from '@helper/urlHelpers';
import { ga4Event } from '@helper/gtm';

import scss from './SupplierPackageDetails.module.scss';

const IMAGE_SOURCES = [
    {
        media: '',
        width: 452,
        height: 254,
    },
];

const OffcanvasClientComponent = React.lazy(() => import('react-bootstrap/Offcanvas'));

const SupplierPackageDetails = () => {
    const { facebookAppId, enableGTMTracking, selectedPackageId, suppliersToHide, region, saveOrder, setSelectedPackageId, findSupplierByPackageId, getRedeemWedPoints } = useVwp();
    const {setNotice, setDelay, onClose} = useContext(NotificationContext);
    
    const [supplier, setSupplier] = useState<VwpSupplier | null>(null);
    const [isSupplierAvailable, setIsSupplierAvailable] = useState<boolean>(true);
    const [packageOrder, setPackageOrder] = useState<VwpOrderCategory>({ packageItemId: 0, categoryId: 0, extras: [] });
    const [addingToCart, setAddingToCart] = useState<boolean>(false);
    const [redeemWedPoints, setRedeemWedPoints] = useState<boolean>(false);

    useEffect(() => {
        const setSupplierAndPackage = async () => {
            if(!!selectedPackageId) {
                // Don't reload the supplier if the selected package belongs to the currently loaded supplier
                if(!!supplier && supplier.packageItems.length > 0 && supplier.packageItems.some(p => p.id === selectedPackageId)) { 
                    setIsSupplierAvailable(!suppliersToHide.includes(supplier.supplierId));    
                    setPackageOrder({ 
                        ...packageOrder,  
                        packageItemId: selectedPackageId,
                        extras: []
                    });
                    ga4Event('package_open', { supplierName: supplier.supplierName, packageName: supplier?.packageItems.find(p => p.id === selectedPackageId)?.name }, enableGTMTracking);
                    return; 
                }
    
                // Get supplier ID and category details from package ID
                var supplierResult = findSupplierByPackageId(selectedPackageId);
    
                if(!!supplierResult) {
                    //Get supplier details
                    const result = await getVwpSupplier(supplierResult.supplierId);

                    if(!!result) {
                        setPackageOrder({ 
                            packageItemId: selectedPackageId,
                            categoryId: supplierResult?.categoryId ?? 0,
                            extras: []
                        });

                        const catSlug = !!supplierResult?.categorySlug ? supplierResult.categorySlug : '';
                        const regionSlug = !!region?.Slug ? region.Slug : '';

                        const items = !!supplierResult?.packageIds ?
                            result.packageItems = result.packageItems.filter(item => 
                                supplierResult?.packageIds.includes(item.id)
                            ) : [];

                        setSupplier({ ...result,
                            packageItems: items,
                            supplierUrl: result.supplierUrl.replace('categorySlug', catSlug).replace('regionSlug', regionSlug),
                        });

                        // Check if supplier is available on wedding date
                        setIsSupplierAvailable(!suppliersToHide.includes(result.supplierId));

                        ga4Event('package_open', { supplierName: result.supplierName, packageName: supplier?.packageItems.find(p => p.id === selectedPackageId)?.name }, enableGTMTracking);
                    } 
                    else {
                        console.error(`Could not retrieve supplier ${supplierResult?.supplierId}`);
                        setSupplier(null);
                        resetPackageOrder();
                    }
                }
                else {
                    console.error(`Error: Package ID not found ${selectedPackageId}`);
                    setSelectedPackageId(null);
                    setSupplier(null);
                    resetPackageOrder();
                }
            }
            else {
                setSupplier(null);
                setIsSupplierAvailable(false);
                resetPackageOrder();
            }

            setRedeemWedPoints(await getRedeemWedPoints());
        };

        setSupplierAndPackage();        
    }, [selectedPackageId, region, suppliersToHide]);

    const resetPackageOrder = () => {
        setPackageOrder({ packageItemId: 0, categoryId: 0, extras: [] });
    };

    const changeExtra = (extraId: number, value: number) => {
        setPackageOrder(packageOrder => {    
            const hasExtra = packageOrder.extras.find(e => e.extraId === extraId);
            const extras = hasExtra ?
                packageOrder.extras.map(extra => {
                    if(extra.extraId === extraId) {
                        return {
                            extraId,
                            quantity: value,
                        }
                    }
                    return extra;
                }).filter(({ quantity }) => quantity > 0)
                : 
                [...packageOrder.extras, {
                    extraId,
                    quantity: value,
                }];
            return {
              ...packageOrder,
              extras: extras
            }
        })
    };

    const handleAddToCart = async (useWedPoints: boolean) => {
        setAddingToCart(true);
        await saveOrder(packageOrder, useWedPoints);
        
        if (!!window) {
            const event = new CustomEvent('incrementCartCount', { detail: { useWedPoints: useWedPoints } });
            window.dispatchEvent(event);
        }

        ga4Event('add_to_cart', { supplierName: supplier?.supplierName, packageName: supplier?.packageItems.find(p => p.id === selectedPackageId)?.name, useWedPoints: (!redeemWedPoints && useWedPoints).toString() }, enableGTMTracking);

        setDelay(5000);
        setNotice(<Notification onClose={onClose}>{supplier?.packageItems.find(p => p.id === selectedPackageId)?.name ?? 'Package'} added to cart</Notification>)

        setAddingToCart(false);
        handlePackageDetailsHide();

        setSessionCookie(VWP_COOKIE_REGION, removeQueryParam('packageId', document.location.href));
    }

    const handlePackageDetailsHide = () => {
        setSelectedPackageId(null);
    }

    return (
        <>
            {!!selectedPackageId && <ClientOnly<OffcanvasProps>
                fallback={<LoadingArea />}
                Component={OffcanvasClientComponent}
                show
                placement={'end'}
                name={'Supplier Package'}
                enforceFocus={false}
                onHide={handlePackageDetailsHide}
                className={scss.offcanvas}
            >
                <div className={scss.flyoutContainer}>
                    <div className={scss.header}>
                        <Button variant={'outline'} className='fw-bold align-items-center px-0' onClick={handlePackageDetailsHide}>
                            <div className={scss.closeIcon}>✕</div>
                        </Button>
                    </div>
                    <div className={scss.body}>
                        {!!selectedPackageId && !!supplier ?
                            <>
                                <SupplierDetails supplier={supplier} />
                                <SupplierPackages 
                                    packages={supplier?.packageItems ?? []} 
                                    packageOrder={packageOrder} 
                                    supplierName={supplier.supplierName ?? ''}
                                    changeExtra={changeExtra} />
                                <div className={scss.sharePackage}>
                                    <div className={scss.shareText}><span>Need a second opinion?&nbsp;</span><span>Share your package.</span></div>
                                    <SharePackageButton
                                        packageName={supplier?.packageItems.find(p => p.id === selectedPackageId)?.name ?? 'Easy Weddings Package'}
                                        facebookAppId={!!facebookAppId ? facebookAppId : ''}
                                    />
                                </div>
                                <div className={`${scss.uspRow} bg-premium row`}>
                                    <div className="col-12 col-md-6 pb-3">
                                        <div className={scss.usp}>
                                            <img src={getAsset("icons/vwp/verifiedSupplier.svg")} width="25" alt="Verified Suppliers" /> Verified Suppliers
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-6 pb-3">
                                        <div className={scss.usp}>
                                            <img src={getAsset("icons/vwp/InterestFreeTerms.svg")} width="25" alt="Interest Free Terms" /> Interest Free Terms
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-6 pb-3">
                                        <div className={scss.usp}>
                                            <img src={getAsset("icons/vwp/BookingGuarantee.svg")} width="25" alt="Booking Guarantee" /> Booking Guarantee
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-6 pb-3">
                                        <div className={scss.usp}>
                                            <img src={getAsset("icons/vwp/TimeSavings.svg")} width="25" alt="Massive Time Savings" /> Massive Time Savings
                                        </div>
                                    </div>
                                </div>
                                <div className={scss.filler}></div>
                                <SupplierPackageSummary 
                                    packageOrder={packageOrder} 
                                    selectedPackage={supplier?.packageItems.find(p => p.id === selectedPackageId) ?? null} 
                                    isAvailable={isSupplierAvailable}
                                    addingToCart={addingToCart}
                                    handleAddToCart={handleAddToCart}
                                    redeemWedPoints={redeemWedPoints}
                                />
                            </> : 
                            <LoadingArea />
                        }
                    </div>
                </div>
            </ClientOnly>}
        </>
    );
}

const SupplierDetails = ({ supplier }: SupplierDetailsProps) => {
    const [showSupplierVideoModal, setShowSupplierVideoModal] = useState<boolean>(false);

    const SupplierVideoModal = React.lazy(() => import('./SupplierVideoModal'));

    const hideVideoModal = useCallback(() => {
        setShowSupplierVideoModal(false)
    }, [setShowSupplierVideoModal]);

    const showVideoModal = useCallback(() => {
        setShowSupplierVideoModal(true)
    }, [setShowSupplierVideoModal]);

    return (
        <>
        <div className={scss.supplierDetailsContainer}>
            {!!supplier.supplierVideoUrl && 
                <Button variant={'link'} className={scss.videoLink} onClick={showVideoModal}>
                    <div className="d-flex">
                        <img src={getAsset("icons/play-square.svg")} className={scss.videoIcon} width="35" height="28"/>
                        <span>Meet this supplier</span>
                    </div>
                    <span className={scss.buttonSubText}>Watch Video Now</span>
                </Button>
            }
            <div className={scss.supplierSlider}>
              {supplier.supplierImages.length <= 1 ?
                <ResponsiveImage
                  src={supplier.supplierImages.length == 1 ? supplier.supplierImages[0] : supplier.supplierListingImage}
                  sources={IMAGE_SOURCES}
                  className={`${scss.image}`}
                  alt={supplier.supplierName}
                  onDragStart={e => e.preventDefault()}
                  loading="lazy"
                  />
              : <ImageSlider
                  lazy
                  isHiddenControls={true}
                  images={supplier.supplierImages}
                  sources={IMAGE_SOURCES}
                />}
            </div>
            <div className={`d-flex w-100 ${scss.topRow}`}>
                <div className={`${scss.rating}`}>
                    <Icon icon={faStar} className={scss.ratingIcon} style={{maxWidth: '1rem', maxHeight: '1rem'}}/>
                    {supplier?.supplierReviewRating ?? ''}
                </div>
                <div className='ms-auto ps-2 text-nowrap'>
                    <a href={supplier.supplierUrl} target="_blank">Supplier Page &rarr;</a>
                </div>
            </div>
            <div className='d-flex align-items-baseline'>
                <div>
                    <h4>{supplier.supplierName}</h4>
                </div>
            </div>
        </div>
        {!!supplier.supplierVideoUrl && showSupplierVideoModal && <ClientOnly<SupplierVideoModalProps> Component={SupplierVideoModal} videoUrl={supplier.supplierVideoUrl} onHideModal={hideVideoModal} />}
        </>
    );
}

export default SupplierPackageDetails